import {
  HurdlrExpenseTransactionsFilters,
  HurdlrIncomeTransactionsFilters,
  HurdlrIncomeAPITransactions,
  HurdlrExpenseAPITransactions,
  ExpenseTransactionUpdateData,
  IncomeTransactionUpdateData,
  PostMutateVariables,
} from 'hooks/api/useHurdlrTransactions';
import queryString from 'query-string';
import { api, apiV2 } from './axios';

export interface IHurdlrIncomeTransactionItem {
  id: number;
  status: string;
  type: string;
  date: string;
  amount: number;
  state: string;
  salesTaxAmount: number | null;
  description: string | null;
  businessId: number;
  clientId: number;
  clientName: string;
  invoiceId: number | null;
  apiName: string;
  apiPaymentId: string;
  apiAccountId: string | null;
  apiAccountNo: string;
  apiAccountName: string;
  apiInstitutionId: string | null;
  plaidItemAccountId: number;
  bankAccountId: number;
  bankDescription: string | null;
  currencyType: string;
  revenueAccountId: number;
  revenueAccountNo: string;
  revenueAccountName: string;
  customData: {
    note: string | null;
  };
  noteCount: number;
  adminUnread: boolean;
  customerUnread: boolean;
}

export interface IHurdlrExpenseTransactionItem {
  id: number;
  status: string;
  type: string;
  date: string;
  amount: number;
  state: string;
  salesTaxAmount: number | null;
  description: string | null;
  vendorId: number;
  vendorName: string;
  businessId: number;
  clientId: number;
  clientName: string;
  categoryId: number;
  categoryName: string;
  categoryAccountNo: string;
  categoryAccountName: string;
  personalCategoryID: number | null;
  personalCategoryName: string | null;
  invoiceId: number | null;
  confidence: string | null;
  frequency: string;
  recurringParentId: number | null;
  isParent: boolean;
  apiName: string;
  apiExpenseId: string;
  plaidCategoryId: number | null;
  apiAccountId: string | null;
  apiAccountNo: string | null;
  apiAccountName: string | null;
  apiInstitutionId: string | null;
  plaidItemAccountId: number | null;
  bankAccountId: number | null;
  bankDescription: string;
  customData: {
    note: string | null;
  };
  childExpenses: ChildExpense[] | null;
  noteCount: number;
  adminUnread: boolean;
  customerUnread: boolean;
}

export interface ChildExpense {
  id: number;
  status: string;
  type: string;
  amount: number;
  businessId: number;
  clientId: number;
  categoryId: number;
  personalCategoryId: number;
  invoiceId: number;
  description: string;
  salesTaxAmount: number;
  state: string;
}

export interface IHurdlrIncomeCategoryItem {
  id: number;
  status: string;
  accountNo: string;
  parentAccountNo: string;
  type: string;
  name: string;
  level: number;
  hasChilds: boolean;
  taxCategory: string;
  taxCategoryName: string;
  assetType: string;
  liabilityType: string;
  equityType: string;
  expenseType: string;
  incomeType: string;
  systemGenerated: boolean;
}

export interface IHurdlrExpenseCategoryItem {
  id: number;
  name: string;
  precedence: number;
  TaxCategory: TaxCategory | null;
  hasChilds: boolean;
  parentCategoryID: number;
  status: string;
  systemGenerated: boolean;
  customData: Object | null;
  glAccountNo: string;
  glAccountName: string;
  accountId: number;
}

interface TaxCategory {
  id: number;
  name: string;
  displayName: string;
  form1040SchedCLineNumber: string;
  form1040SchedCDisplayName: string;
  t2125LineNumber: string;
  t2125DisplayName: string;
  form1065LineNumber: string;
  form1065DisplayName: string;
  form1120SLineNumber: string;
  form1120SDisplayName: string;
}

export interface HurdlrVendor {
  id: number;
  name: string;
  status: string;
  defaultCategoryId: number;
  defaultBusinessId: number;
  defaultClientId: number;
}

export const getHurdlrExpenseTransactions = async (
  businessId: string,
  params: Partial<HurdlrExpenseTransactionsFilters>,
): Promise<HurdlrExpenseAPITransactions> => {
  const { data } = await apiV2.get<HurdlrExpenseAPITransactions>(
    `/hurdlr/${businessId}/expenses?${queryString.stringify(
      params,
    )}`,
  );
  return data;
};

export const getHurdlrIncomeTransactions = async (
  businessId: string,
  params: Partial<HurdlrIncomeTransactionsFilters>,
): Promise<HurdlrIncomeAPITransactions> => {
  const { data } = await apiV2.get<HurdlrIncomeAPITransactions>(
    `/hurdlr/${businessId}/revenues?${queryString.stringify(
      params,
    )}`,
  );
  return data;
};

export const updateExpenseTransaction = async ({
  businessId,
  transactionId,
  categoryId,
  type,
}: ExpenseTransactionUpdateData): Promise<void> => {
  await apiV2.patch(`hurdlr/${businessId}/expenses/${transactionId}`, {
    categoryId,
    type,
  });
};

export const updateIncomeTransaction = async ({
  businessId,
  transactionId,
  revenueAccountId,
  type,
}: IncomeTransactionUpdateData): Promise<void> => {
  await apiV2.patch(`hurdlr/${businessId}/revenues/${transactionId}`, {
    revenueAccountId,
    type,
  });
};

export const getExpenseCategories = async (
  businessId: string,
): Promise<IHurdlrExpenseCategoryItem[]> => {
  const { data } = await apiV2.get<IHurdlrExpenseCategoryItem[]>(
    `/hurdlr/${businessId}/expenses/categories`,
  );
  return data;
};

export const getIncomeCategories = async (
  businessId: string,
): Promise<IHurdlrIncomeCategoryItem[]> => {
  const { data } = await apiV2.get<IHurdlrIncomeCategoryItem[]>(
    `/hurdlr/${businessId}/gl-accounts`,
  );
  return data;
};

export const getTransactionVendors = async (
  businessId: string,
): Promise<HurdlrVendor[]> => {
  const { data } = await apiV2.get<HurdlrVendor[]>(
    `/hurdlr/${businessId}/vendors`,
  );
  return data;
};

export const createTransactionVendor = async (
  businessId: string,
  vendorName: string,
): Promise<{ id: string; name: string }> => {
  const { data } = await apiV2.post<
    { name: string },
    { data: { id: string; name: string } }
  >(`/hurdlr/${businessId}/vendors`, {
    name: vendorName,
  });
  return data;
};

export const createHurdlrExpenseTransaction = async ({
  businessId,
  formTransactionData,
}: PostMutateVariables) => {
  await apiV2.post<FormData>(
    `/hurdlr/${businessId}/expenses`,
    formTransactionData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000,
    },
  );
};

export const getHurdlrUnknownTransactionCount = async (
  businessId: string,
  year: number,
): Promise<{ count: number }> => {
  const { data } = await api.get<{ count: number }>(
    `/hurdlr/transactions/${businessId}/count-unknown`,
    {
      params: {
        year,
      },
    },
  );
  return data;
};

export const updateHurdlrExpenseTransactionNote = async (
  businessId: string,
  transactionId: number,
  noteId: string,
  note: string,
) => {
  await apiV2.patch(`hurdlr/${businessId}/expenses/${transactionId}/notes/${noteId}`, {
    note,
  });
};

export const updateHurdlrIncomeTransactionNote = async (
  businessId: string,
  transactionId: number,
  noteId: string,
  note: string,
) => {
  await apiV2.patch(`hurdlr/${businessId}/revenues/${transactionId}/notes/${noteId}`, {
    note,
  });
};

export const markExpenseTransactionAsRead = async (
  businessId: string,
  transactionId: number,
) => {
  await apiV2.patch(
    `hurdlr/${businessId}/expenses/${transactionId}/notes/read`,
  );
};

export const markIncomeTransactionAsRead = async (
  businessId: string,
  transactionId: number,
) => {
  await apiV2.patch(
    `hurdlr/${businessId}/revenues/${transactionId}/notes/read`,
  );
};

export const postHurdlrExpenseTransactionNote = async (
  businessId: string,
  transactionId: number,
  note: string,
) => {
  const { data } = await apiV2.post(
    `hurdlr/${businessId}/expenses/${transactionId}/notes`,
    {
      note,
    },
  );

  return data;
};

export const postHurdlrIncomeTransactionNote = async (
  businessId: string,
  transactionId: number,
  note: string,
) => {
  const { data } = await apiV2.post(
    `hurdlr/${businessId}/revenues/${transactionId}/notes`,
    {
      note,
    },
  );

  return data;
};

export const getHurdlrExpenseTransactionNote = async (
  businessId: string,
  transactionId: number,
) => {
  const { data } = await apiV2.get(
    `hurdlr/${businessId}/expenses/${transactionId}/notes`,
  );
  return data;
};

export const getHurdlrIncomeTransactionNote = async (
  businessId: string,
  transactionId: number,
) => {
  const { data } = await apiV2.get(
    `hurdlr/${businessId}/revenues/${transactionId}/notes`,
  );
  return data;
};
